import React, { useEffect, useReducer, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import { IconButton, Button, InputAdornment } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { RemoveCircleRounded, Edit } from '@material-ui/icons';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { useDispatch } from 'react-redux';
import {
  allRoomTypesSelector,
  allUpgradesSelector,
  fetchAllAccommodations,
  selectedHotelCurrencySelector,
} from '../../../../property/propertySlice';
import { formDataValidate } from '../../../../../utils/formUtils';
import CustomSelect from '../../../../../shared/form-controls/custom-select/Custom-Select';
import './Upgrade-Create.scss';
import { createNewUpgrade, updateHotelUpgrade } from '../../../../../utils/ServicesAPIUtils';
import { useSelector } from 'react-redux';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const UpgradeCreate = (props) => {
  const dispatch = useDispatch();
  const currency = useSelector(selectedHotelCurrencySelector);
  const { toggleDrawer, upgrade, hotelId, showLoader = () => {} } = props;
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [upgradeValueError, setUpgradeValueError] = useState('');
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    roomType: {
      value: '',
      fieldName: 'Select a Room type',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    roomUpgrade: {
      value: '',
      fieldName: 'Select Room upgrades',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    upgradeRate: {
      value: '',
      fieldName: 'Upgrade Rate',
      errors: {},
      validations: ['positiveOnlyWithZero'],
      tab: 0,
    },
    upgradePercent: {
      value: '',
      fieldName: 'Upgrade Percent',
      errors: {},
      validations: ['positiveOnlyWithZero'],
      tab: 0,
    },
    upgrades: {
      value: [],
      fieldName: 'upgrades',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
  });

  const [isSubmitted, setSubmitted] = useState(false);
  const originalRoomTypes = useSelector(allRoomTypesSelector);
  const [dropDownUpgradeList, setDropDownUpgradeList] = useState([]);
  const [dropDownRoomTypeList, setDropDownRoomTypeList] = useState([]);
  const originalUpgrades = useSelector(allUpgradesSelector);
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    const roomTypesList = originalRoomTypes || [];
    const existingUpgradesList = originalUpgrades || [];
    const existingUpgradeItems = (formData && formData.upgrades && formData.upgrades.value) || [];

    const roomTypeOptions = roomTypesList
      .filter(
        (roomType) =>
          !existingUpgradesList.some((tempUpgrade) => tempUpgrade.roomTypeId === roomType.id),
      )
      .map((roomType) => {
        return {
          id: roomType.id,
          value: roomType.id,
          name: roomType.name,
        };
      });

    let roomUpgradeOptions = roomTypesList.map((roomType) => {
      return {
        id: roomType.id,
        value: roomType.id,
        name: roomType.name,
      };
    });

    if (upgrade) {
      const upgradeRoomType = roomTypesList.find((roomType) => roomType.id === upgrade.roomTypeId);
      if (upgradeRoomType) {
        roomTypeOptions.push({
          id: upgradeRoomType.id,
          value: upgradeRoomType.id,
          name: upgradeRoomType.name,
        });
      }
    }

    // When not editing, prevent selecting a room type already used in upgrades.
    // While editing, show all options so the current upgrade type is always available.
    if (editingIndex === null || editingIndex === undefined) {
      roomUpgradeOptions = roomUpgradeOptions.filter(
        (dropdown) => !existingUpgradeItems.some((item) => item?.upgradeType === dropdown?.id),
      );
    }

    // Never allow selecting the same room type as an upgrade to itself
    const selectedRoomTypeId = formData?.roomType?.value;
    if (selectedRoomTypeId) {
      roomUpgradeOptions = roomUpgradeOptions.filter(
        (dropdown) => dropdown.id !== selectedRoomTypeId,
      );
    }

    setDropDownRoomTypeList(roomTypeOptions);
    setDropDownUpgradeList(roomUpgradeOptions);
  }, [
    originalRoomTypes,
    upgrade,
    originalUpgrades,
    formData?.upgrades?.value,
    formData?.roomType?.value,
    editingIndex,
  ]);

  useEffect(() => {
    if (upgrade) {
      const currFields = { ...formData };
      for (let field in currFields) {
        if (field === 'roomType') {
          currFields[field].value = upgrade['roomTypeId'];
        } else if (
          field === 'roomUpgrade' ||
          field === 'upgradeRate' ||
          field === 'upgradePercent'
        ) {
          currFields[field].validations = [];
          currFields[field].value = '';
        } else if (field === 'upgrades') {
          currFields[field].value = upgrade['upgradeType'];
        }
      }

      setFormData(currFields);
    }
  }, [upgrade]);

  const validate = () => {
    const updatedFields = {};
    const isValid = formDataValidate(formData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const handleSubmit = async (e) => {
    setSubmitted(true);
    e.preventDefault();
    if (validate() && formData?.upgrades?.value?.length > 0) {
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      try {
        dispatch(stepUpLoaderCount());
        toggleDrawer('', false, '');
        if (upgrade) {
          await updateHotelUpgrade(
            hotelId,
            {
              id: upgrade?.id,
              roomTypeId: formData?.roomType.value,
              upgradeType: formData?.upgrades?.value,
            },
            language,
          );
          notificationManager('success', 'Room upgrade updated successfully');
        } else {
          await createNewUpgrade(
            hotelId,
            {
              roomTypeId: formData?.roomType?.value,
              upgradeType: formData?.upgrades?.value,
            },
            language,
          );
          notificationManager('success', 'Room upgrade created successfully');
        }
        dispatch(fetchAllAccommodations(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          upgrade
            ? notificationManager('danger', 'Room upgrade updation failed')
            : notificationManager('danger', 'Room upgrade creation failed');
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  useEffect(() => {
    const isValid = formDataValidate(formData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  const onUpgradeAdd = () => {
    const rateValue = Number(formData?.upgradeRate?.value || 0);
    const percentValue = Number(formData?.upgradePercent?.value || 0);

    const hasRoomUpgrade = !!formData.roomUpgrade.value;
    const hasPositiveRate = rateValue > 0;
    const hasPositivePercent = percentValue > 0;
    const isXorValid =
      hasRoomUpgrade &&
      ((hasPositiveRate && !hasPositivePercent) || (hasPositivePercent && !hasPositiveRate));

    if (!isXorValid) {
      setUpgradeValueError('Either rate or percent must be greater than 0');
      return;
    }

    setUpgradeValueError('');

    if (hasRoomUpgrade) {
      const upgradesArray = [...formData.upgrades.value];

      const newUpgrade = {
        upgradeType: formData.roomUpgrade.value,
        rate: hasPositiveRate ? rateValue : 0,
        percent: hasPositivePercent ? percentValue : 0,
      };

      // Prevent duplicate upgrades for the same upgradeType (except the one currently being edited)
      const duplicateIndex = upgradesArray.findIndex((item, index) => {
        if (!item) return false;
        const sameType = item.upgradeType === newUpgrade.upgradeType;
        const isCurrentEditing =
          editingIndex !== null && editingIndex !== undefined && index === editingIndex;
        return sameType && !isCurrentEditing;
      });

      if (duplicateIndex !== -1) {
        setUpgradeValueError('Selected room upgrade is already added');
        return;
      }

      // If we are editing an existing item, update it in-place; otherwise prepend a new one
      if (editingIndex !== null && editingIndex !== undefined) {
        upgradesArray[editingIndex] = newUpgrade;
      } else {
        upgradesArray.unshift(newUpgrade);
      }

      const updatedObj = {
        ...formData,
        upgrades: {
          ...formData.upgrades,
          value: upgradesArray,
        },
        roomUpgrade: {
          value: '',
          fieldName: 'Select Room upgrades',
          errors: {},
          validations: [],
        },
        upgradeRate: {
          value: '',
          fieldName: 'Upgrade Rate',
          errors: {},
          validations: ['positiveOnlyWithZero'],
        },
        upgradePercent: {
          value: '',
          fieldName: 'Upgrade Percent',
          errors: {},
          validations: ['positiveOnlyWithZero'],
        },
      };

      setFormData(updatedObj);
      setEditingIndex(null);

      setDropDownUpgradeList(
        dropDownUpgradeList.filter(
          (dropdown) => !upgradesArray.some((upgrade) => upgrade.upgradeType === dropdown.id),
        ),
      );
    }
  };

  const onItemEdit = (index) => {
    const upgradesValue = (formData && formData.upgrades && formData.upgrades.value) || [];
    const selectedItem = upgradesValue[index];

    if (!selectedItem) {
      setEditingIndex(null);
      return;
    }

    const formDataCopy = JSON.parse(JSON.stringify(formData));

    // Track which item is currently being edited so Add updates in-place
    setEditingIndex(index);

    formDataCopy.roomUpgrade = {
      ...formDataCopy.roomUpgrade,
      value: selectedItem?.upgradeType || '',
    };

    const selectedRate = Number(selectedItem?.rate || 0);
    const selectedPercent = Number(selectedItem?.percent || 0);

    // Only prefill the field that has a positive value
    formDataCopy.upgradeRate.value = selectedRate > 0 ? selectedRate : '';
    formDataCopy.upgradePercent.value = selectedPercent > 0 ? selectedPercent : '';

    setFormData(formDataCopy);
  };

  const onInfoDelete = (index) => {
    const upgradesArray = [...formData.upgrades.value];
    upgradesArray.splice(index, 1);
    setFormData({
      upgrades: {
        value: upgradesArray,
        fieldName: 'Upgrades',
        errors: {},
        validations: [],
      },
      roomUpgrade: {
        value: '',
        fieldName: 'Select Room upgrades',
        errors: {},
        validations: upgradesArray.length === 0 ? ['required'] : [],
      },
      upgradeRate: {
        value: '',
        fieldName: 'Upgrade Rate',
        errors: {},
        validations: ['positiveOnlyWithZero'],
      },
      upgradePercent: {
        value: '',
        fieldName: 'Upgrade Percent',
        errors: {},
        validations: ['positiveOnlyWithZero'],
      },
    });
  };

  const reorder = (list, sourceIndex, destinationIndex) => {
    const result = [...list];
    const [removed] = result.splice(sourceIndex, 1);
    result.splice(destinationIndex, 0, removed);
    return result;
  };

  const onDragEnd = (dragResult) => {
    if (!dragResult.destination) return;
    const reorderedinfo = reorder(
      formData.upgrades.value,
      dragResult.source.index,
      dragResult.destination.index,
    );
    const currField = { ...formData.upgrades };
    currField.value = reorderedinfo;
    setFormData({ upgrades: currField });
  };

  const rateValue = Number(formData?.upgradeRate?.value || 0);
  const percentValue = Number(formData?.upgradePercent?.value || 0);
  const isUpgradeXorValidForButton =
    !!formData.roomUpgrade.value &&
    ((rateValue > 0 && percentValue === 0) || (percentValue > 0 && rateValue === 0));

  return (
    <form onSubmit={handleSubmit} className='upgrade-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-6'>
            <span className='page-title'>{upgrade ? 'Edit Upgrade' : 'Add Upgrade'}</span>
          </div>
          <div className='col-md-6 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          <div className='row px-3 w-100 mt-3 mb-4'>
            <div className='col-md-12'>
              <span className='page-title-secondary'>
                Select a Room type to map relevant room upgrades
              </span>
            </div>
          </div>
          <div className='row px-3 w-100 d-flex mt-2'>
            <div className='col-md-6 mb-3'>
              <CustomSelect
                item={formData.roomType}
                handleInputChange={(e) => {
                  setFormData({ roomType: e });
                }}
                options={dropDownRoomTypeList}
                hideNullOption={dropDownRoomTypeList.length}
              />
            </div>
            <div className='col-md-6 mb-3'>
              <CustomSelect
                item={formData.roomUpgrade}
                handleInputChange={(e) => {
                  setFormData({ roomUpgrade: e });
                }}
                options={dropDownUpgradeList}
                hideNullOption={dropDownUpgradeList.length}
              />
            </div>
          </div>
          <div className='row px-3 w-100'>
            <div className='col-md-6 mb-3'>
              <CustomTextField
                item={formData.upgradeRate}
                handleInputChange={(e) => {
                  setFormData({
                    upgradeRate: e,
                    upgradePercent: { ...formData.upgradePercent, value: '' },
                  });
                }}
                startAdroment={formData.roomUpgrade.value}
                adromentValue={currency}
                type='number'
                min={0}
                max={10000000}
              />
            </div>
            <div className='col-md-6 mb-3'>
              <CustomTextField
                item={formData.upgradePercent}
                handleInputChange={(e) => {
                  setFormData({
                    upgradePercent: e,
                    upgradeRate: { ...formData.upgradeRate, value: '' },
                  });
                }}
                startAdroment={formData.roomUpgrade.value}
                adromentValue='%'
                type='number'
                min={0}
                max={100}
              />
            </div>
          </div>
          <div className='row px-3 w-100'>
            {isSubmitted && formData.upgrades.value.length === 0 && (
              <div className='col-md-12 mb-2'>
                <span className='upgrades-alert'>Atleast 1 upgrade should be added</span>
              </div>
            )}
            {upgradeValueError && (
              <div className='col-md-12 mb-2'>
                <span className='upgrades-alert'>{upgradeValueError}</span>
              </div>
            )}
            <div className='col-md-12 mb-3'>
              <Button
                variant='outlined'
                color={isUpgradeXorValidForButton ? 'primary' : 'secondary'}
                disabled={!isUpgradeXorValidForButton}
                onClick={onUpgradeAdd}
              >
                Add
              </Button>
            </div>
          </div>
          <div className='row px-3 w-100'>
            <div className='col-md-12 mb-3 drag-item'>
              <DragDropContext onDragEnd={(e) => onDragEnd(e)}>
                <Droppable droppableId='droppableSpaContactInfo'>
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} className='col-md-11' ref={provided.innerRef}>
                      {formData.upgrades.value.map((item, index) => (
                        <Draggable key={index} draggableId={'dragId' + index} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className='row additional-info-list-item mb-3 custom-duration-box align-items-center'
                              key={index}
                            >
                              <IconButton aria-label='drag' disabled={true} className='col-1'>
                                <DragIndicatorIcon fontSize='large' />
                              </IconButton>
                              <div className='col-md-5'>
                                <TextField
                                  id='additional-info-field-edit'
                                  className='col-md-12'
                                  value={
                                    originalRoomTypes.find((val) => val.id === item.upgradeType)
                                      ?.name
                                  }
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                />
                              </div>
                              <div className='col-md-4'>
                                <TextField
                                  id='additional-info-value-edit'
                                  className='col-md-12'
                                  value={item.percent > 0 ? item.percent : item.rate}
                                  InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                      <InputAdornment position='start'>
                                        {item.percent > 0 ? (
                                          <span>%</span>
                                        ) : (
                                          <span>{currency}</span>
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>
                              <div className='col-md-1 d-flex justify-content-end align-items-end pe-0 ms-3'>
                                <IconButton onClick={(e) => onItemEdit(index)}>
                                  <Edit fontSize='small' />
                                </IconButton>
                                <IconButton onClick={(e) => onInfoDelete(index)}>
                                  <RemoveCircleRounded
                                    className='additional-info-remove'
                                    fontSize='small'
                                  />
                                </IconButton>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            <Button
              variant='contained'
              type='submit'
              color='primary'
              className='me-3'
              size='large'
              disabled={isTabInValid}
            >
              {upgrade ? 'Update' : 'Add'}
            </Button>
            <Button
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default UpgradeCreate;
